/**
 * ConsultationSection — Morgan Legal Group
 * Design: Split layout — form left, consultation room image right
 * Navy form with gold accents, trust signals
 */
import { useEffect, useRef, useState } from "react";
import { Phone, Mail, MapPin, Clock, CheckCircle } from "lucide-react";
import CalendarEmbed from "@/components/CalendarEmbed";

export default function ConsultationSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="consultation" className="py-20 lg:py-28 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-0 shadow-2xl">
          {/* Left — Form */}
          <div
            className={`bg-navy p-8 lg:p-12 xl:p-16 transition-all duration-700 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            }`}
          >
            <div className="section-label mb-4 text-gold">
              Free Consultation
            </div>
            <h2 className="font-display text-white text-3xl lg:text-4xl font-bold leading-tight mb-3">
              Schedule Your{" "}
              <span className="text-gold italic">Free Consultation</span>
            </h2>
            <p className="font-body text-white/60 text-sm leading-relaxed mb-8">
              Pick a date and time that works for you — book your free consultation with a senior attorney directly below. No obligation, no pressure.
            </p>

            <div className="bg-white shadow-lg overflow-hidden">
              <CalendarEmbed height={640} />
            </div>
            <p className="font-body text-white/40 text-xs text-center mt-4">
              {/* Планировщик здесь — Calendly (см. CalendarEmbed): именно на его
                  событии висит конверсия Google Ads. Прежняя подпись называла
                  Legal OS, то есть чужой продукт. */}
              Secure scheduling. Prefer to call? <a href="tel:8885291315" className="text-gold hover:underline">888-529-1315</a>.
            </p>
          </div>

          {/* Right — Image (top) + Contact Info (below, no overlap) */}
          <div
            className={`relative flex flex-col bg-navy transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
            }`}
          >
            {/* Image panel — Russel Morgan portrait, full and uncovered */}
            <div className="relative h-72 lg:h-80 xl:h-96 bg-navy overflow-hidden flex-shrink-0">
              <picture>
                <source srcSet="/img/attorneys/russel-morgan-contact.webp" type="image/webp" />
                <img
                  src="/img/attorneys/russel-morgan-contact.jpg"
                  alt="Russel Morgan, Esq. — Founder, Morgan Legal Group"
                  width="640"
                  height="640"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
              </picture>
              {/* Subtle bottom fade into the contact block below */}
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-navy to-transparent pointer-events-none" />
            </div>

            {/* Contact info — solid navy block below the photo */}
            <div className="relative flex-1 p-6 lg:p-8 xl:p-10">
              <h3 className="font-display text-white text-xl font-bold mb-5">Contact Information</h3>
              <div className="space-y-3">
                {[
                  { icon: Phone, text: "888-529-1315", href: "tel:8885291315", label: "Call us" },
                  { icon: Mail, text: "contact@morganlegalny.com", href: "mailto:contact@morganlegalny.com", label: "Email us" },
                  { icon: MapPin, text: "15 Maiden Ln #905, New York, NY 10038", href: "https://g.co/kgs/swwPdvP", label: "Visit us" },
                  { icon: Clock, text: "Mon–Fri: 9:00 AM – 6:00 PM", href: null, label: "Hours" },
                ].map(({ icon: Icon, text, href, label }) => (
                  <div key={label} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-gold/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon className="w-3.5 h-3.5 text-gold" />
                    </div>
                    {href ? (
                      <a href={href} className="font-body text-white/80 text-sm hover:text-gold transition-colors duration-200 leading-snug">
                        {text}
                      </a>
                    ) : (
                      <span className="font-body text-white/80 text-sm leading-snug">{text}</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Trust signals */}
              <div className="mt-6 pt-5 border-t border-white/10 grid grid-cols-3 gap-3">
                {["Free Consult", "Flexible Scheduling", "Reply in 2 Business Days"].map((signal) => (
                  <div key={signal} className="text-center">
                    <CheckCircle className="w-4 h-4 text-gold mx-auto mb-1" />
                    <p className="font-body text-white/70 text-xs">{signal}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
